import { useState } from "react";
import { toastService } from "@services/ToastService";
import { templateService } from "@services/TemplateServices";

interface TemplatePayload {
  name: string;
  description: string;
  prompt: string;
  template_type: string | null;
  company_id?: number | null;
}

export const useTemplate = () => {
  const [loading, setLoading] = useState(false);

  const saveTemplate = async (
    payload: TemplatePayload,
    options?: {
      templateId?: string;
      isChoosing?: boolean;
      projectId?: string;
      reportName?: string;
      checkedItems?: any;
    }
  ) => {
    setLoading(true);
    try {
      const response = options?.templateId
        ? await templateService.updateTemplate(options.templateId, payload)
        : await templateService.createTemplate(payload);

      toastService.success(
        `Template ${options?.templateId ? "updated" : "created"} successfully`
      );

      return response;
    } catch (error) {
      toastService.error("Failed to save template");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplatTypes = async () => {
    setLoading(true);
    try {
      const response = await templateService.getTemplateTypes();
      return response || [];
    } catch (error) {
      console.error("Failed to fetch templates");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async (
    mine: boolean,
    params?: {
      order?: string;
      template_type?: string;
      offset?: number;
      limit?: number;
    }
  ) => {
    setLoading(true);
    try {
      const response = mine
        ? await templateService.getMyTemplates(params)
        : await templateService.getAllTemplates(params);
      return response || [];
    } catch (error) {
      console.error("Failed to fetch templates");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const deleteTemplate = async (templateId: number | string) => {
    setLoading(true);
    try {
      await templateService.deleteTemplate(templateId);
      toastService.success("Template deleted");
    } catch (error) {
      toastService.error("Failed to delete template");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const getTemplateById = async (templateId: string) => {
    try {
      return await templateService.getTemplateById(templateId);
    } catch (error) {
      console.error("Failed to fetch template");
      throw error;
    }
  };
  const generateFromFile = async (file: File): Promise<string | null> => {
    try {
      const response = await templateService.generateFromFile(file);
      return response?.task_id || null;
    } catch (error) {
      toastService.error("Failed to upload and generate template");
      throw error;
    }
  };
  return {
    loading,
    saveTemplate,
    fetchTemplatTypes,
    fetchTemplates,
    deleteTemplate,
    getTemplateById,
    generateFromFile,
  };
};
